import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { getQuery, getQueryArham } from "../../redux/actions/query.action";
import "../../css/chatbot.scss";
import Loading from "./loading";

import agentai from "../../images/voice.svg";
import userIcon from "../../images/user.png";

const initialMessages = {
  supabase: <>Using Method: Postgres Vectorization (Supabase)<br />How can I assist you?</>,
  Postgres: <>Using Method: Postgres without Vectorization<br />Upcoming</>,
  arham: <>Using Method: Shopify API<br />How can I assist you?</>,
  talha: <>Using Method: MCP + Shopify API<br />Upcoming</>,
};

function VoiceBot({ source }) {
  const dispatch = useDispatch();
  const { account, search } = useSelector((state) => ({
    account: state.account,
    search: state.search,
  }));

  const [isMobile, setIsMobile] = useState(false);

  const [form] = Form.useForm();
  const chatContainerRef = useRef(null);

  const [chat, setChat] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [firstMessageSent, setFirstMessageSent] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [show, setShow] = useState(true);

  useEffect(() => {
    if (search?.payload) {
      setChat((prev) => [...prev, { user: search.payload, server: "" }]);
    }
  }, [search]);

  useEffect(() => {
    // Detect mobile device
    const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    setIsMobile(isMobileDevice);
  }, []);

  useEffect(() => {
    if (show && query && form) {
      form.setFieldsValue({ message: query });
      form.getFieldInstance("message")?.focus();
    }
  }, [show, query, loading]);

  useEffect(() => {
    setChat([]);
    setQuery("");
    setFirstMessageSent(false);
    form.resetFields();
  }, [source]);

  useEffect(() => {
    chatContainerRef.current?.scrollTo({ top: chatContainerRef.current.scrollHeight, behavior: "smooth" });
  }, [chat]);

  useEffect(() => {
    document.body.classList.toggle("static-chat", !isLargeScreen);
  }, [isLargeScreen]);

  const handleSubmit = async () => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;

    setFirstMessageSent(true);
    setLoading(true);
    setChat((prev) => [...prev, { user: trimmedQuery, server: "", loading: true }]);
    form.resetFields();
    setQuery("");

    try {
      let res;
      if (source === "supabase") res = await dispatch(getQuery(trimmedQuery));
      else if (source === "arham") res = await dispatch(getQueryArham(trimmedQuery));
      // else if (source === "api") res = await dispatch(getQueryApi(trimmedQuery));

      const payload = res?.payload;
      const srcdocValue = payload?.match(/srcdoc="([^"]*)"/)?.[1];
      const aiResponse = payload?.response?.message?.trim() || "";
      const products = payload?.response?.products || [];
      const rawProducts = res?.payload?.response?.products || [];
      const aiResults = rawProducts.map((product) => ({
        title: product?.title || "No title",
        mediaUrl: product?.image || "",
        ProductUrl: product?.url || "#",
        variants: [{ price: product?.price || "N/A" }],
      }));

      const cleanedResponse = aiResponse.trim();

      // Modify <a> and <iframe> tags to open https links in new tab
      const modifiedResponse = cleanedResponse
        .replace(/<a\s+([^>]*href="https:\/\/[^"]*")/g, '<a $1 target="_blank" rel="noopener noreferrer"')
        .replace(/<iframe\s+([^>]*src="https:\/\/[^"]*")/g, '<iframe $1 target="_blank" rel="noopener noreferrer"');

      setChat((prevChat) => {
        const updatedChat = [...prevChat];
        const lastUserMessage = updatedChat[updatedChat.length - 1];
        if (lastUserMessage) {
          lastUserMessage.server = srcdocValue || modifiedResponse;
          lastUserMessage.loading = false;
          if (aiResults.length > 0) lastUserMessage.products = aiResults;
        }
        return updatedChat;
      });
      // const aiResults = rawProducts.map((product) => ({
      //   title: product?.title || "No title",
      //   mediaUrl: product?.image || "",
      //   ProductUrl: product?.url || "#",
      //   variants: [{ price: product?.price || "N/A" }],
      // }));
      // const cleanedResponse = aiResponse.trim();
      // setChat((prevChat) => {
      //   const updatedChat = [...prevChat];
      //   const lastUserMessage = updatedChat[updatedChat.length - 1];
      //   if (lastUserMessage) {
      //     lastUserMessage.server = srcdocValue || cleanedResponse;
      //     lastUserMessage.loading = false;
      //     if (aiResults.length > 0) lastUserMessage.products = aiResults;
      //   }
      //   return updatedChat;
      // });
    } catch (err) {
      console.error("Error fetching AI response:", err);
      setChat((prev) => [...prev, {
        user: "",
        server: "Error fetching AI response. Please try again.",
        loading: false,
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`chat-box-wrapper ${show ? "" : "toggle-chat-bo"} ${isLargeScreen ? "large-ai-bo" : ""}`}>
      <section className="msger">
        <div className="chat-content-area">
          {!firstMessageSent && (
            <div className="assist-you">{initialMessages[source] || "How can I assist you?"}</div>
          )}

          <div className="msger-chat" ref={chatContainerRef}>
            {chat.map((ct, i) => (
              <React.Fragment key={`${i}-${ct.user || ct.server || i}`}>
                {ct.user && (
                  <div className="msg right-msg">
                    <div className="msg-img" style={{ backgroundImage: `url(${userIcon})` }} />
                    <div className="msg-bubble">
                      <div className="msg-info">
                        <div className="msg-info-name">{account?.payload?.Data?.name || "User"}</div>
                      </div>
                      <div className="msg-text">{ct.user}</div>
                    </div>
                  </div>
                )}

                {(ct.server || ct.loading) && (
                  <div className="msg left-msg">
                    <div className="bot-image-area">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="text-text"
                        data-testid="icon-sparkle-filled"
                        stroke="none"
                        style={{ width: "20px", height: "20px" }}
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M10.9711 4.90751C10.8931 4.64523 10.7206 4.5 10.4839 4.5C10.2471 4.49956 10.0715 4.64388 9.99619 4.90617C9.77236 5.68628 9.55168 6.46787 9.32794 7.24798C8.61425 9.73641 7.04152 11.4147 4.58927 12.2337C3.70977 12.5272 2.80719 12.7524 1.91414 13.0062C1.72111 13.0612 1.55261 13.1377 1.5 13.3561V13.6353C1.54814 13.8449 1.70863 13.9285 1.89273 13.9812C2.68611 14.2073 3.48101 14.4281 4.27322 14.6582C6.68445 15.3578 8.34098 16.8815 9.17323 19.2578C9.49804 20.1848 9.72555 21.1412 9.99484 22.0848C10.0501 22.2783 10.1268 22.4471 10.3443 22.5H10.6221C10.8302 22.4508 10.9149 22.2913 10.9676 22.1062C11.1922 21.3182 11.4147 20.5298 11.6389 19.7417C12.3173 17.3561 13.8027 15.6965 16.1297 14.8476C17.0886 14.4977 18.0911 14.2681 19.0732 13.9817C19.3465 13.9021 19.4931 13.7345 19.4998 13.5071C19.5065 13.2775 19.3505 13.0911 19.0732 13.0112C18.2884 12.7847 17.5021 12.5615 16.7163 12.3378C14.3876 11.6747 12.7498 10.2287 11.8742 7.96423C11.4914 6.97378 11.2742 5.92613 10.9711 4.90751ZM14.6884 13.4987C12.8271 12.6269 11.3944 11.2175 10.4837 9.28687C9.60343 11.1665 8.19216 12.5937 6.28005 13.4935C8.15095 14.3715 9.58039 15.7826 10.481 17.7029C11.3539 15.8365 12.7617 14.4019 14.6884 13.4987Z"
                          fill="white"
                        />
                        <path
                          d="M14.6884 13.4987C12.8271 12.6269 11.3944 11.2175 10.4837 9.28687C9.60343 11.1665 8.19216 12.5937 6.28005 13.4935C8.15095 14.3715 9.58039 15.7826 10.481 17.7029C11.3539 15.8365 12.7617 14.4019 14.6884 13.4987Z"
                          fill="white"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M18.4928 1.5C18.598 1.5 18.6746 1.56455 18.7093 1.68112C18.7553 1.8356 18.7968 1.99158 18.8383 2.14754C18.9184 2.44864 18.9986 2.74967 19.1107 3.03967C19.4999 4.04613 20.2277 4.68877 21.2627 4.98349C21.3251 5.00128 21.3876 5.01905 21.4501 5.03683C21.7369 5.11845 22.0238 5.2001 22.3102 5.28278C22.4334 5.31829 22.5028 5.40114 22.4998 5.50318C22.4968 5.60426 22.4316 5.67874 22.3102 5.71409C22.1704 5.75487 22.0296 5.79306 21.8888 5.83125C21.5902 5.91226 21.2916 5.99328 21.0019 6.09898C19.9678 6.4763 19.3076 7.21386 19.0061 8.27415C18.9613 8.43159 18.9167 8.58905 18.872 8.74652C18.8174 8.93936 18.7627 9.13222 18.7078 9.32504C18.6844 9.4073 18.6467 9.47822 18.5542 9.5H18.4307C18.3341 9.4766 18.3 9.40154 18.2754 9.31559C18.2396 9.18993 18.2054 9.06377 18.1712 8.93759C18.0913 8.64263 18.0114 8.34763 17.9103 8.05907C17.5404 7.00294 16.8042 6.32574 15.7325 6.01478C15.5077 5.94946 15.2824 5.88584 15.057 5.82221C14.9295 5.78621 14.802 5.75021 14.6746 5.71389C14.5928 5.69046 14.5214 5.65333 14.5 5.56017V5.43606C14.5234 5.33897 14.5983 5.30502 14.684 5.28057C14.8014 5.24721 14.9191 5.21497 15.0368 5.18272C15.3173 5.10592 15.5977 5.02911 15.873 4.93721C16.9628 4.57319 17.6619 3.82729 17.979 2.72134C18.0288 2.54798 18.0781 2.37445 18.1275 2.20093C18.1769 2.0274 18.2263 1.85388 18.276 1.68052C18.3095 1.56395 18.3875 1.4998 18.4928 1.5ZM18.4928 3.62754C18.8975 4.48554 19.5342 5.11198 20.3614 5.4995C19.5051 5.90086 18.8795 6.53846 18.4915 7.36798C18.4914 7.36786 18.4914 7.36773 18.4913 7.36761C18.8793 6.53835 19.5048 5.90093 20.361 5.49965C19.5339 5.11218 18.8972 4.48587 18.4925 3.62806C18.4926 3.62789 18.4927 3.62771 18.4928 3.62754Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                    <div className="msg-bubble">
                      <div className="msg-info">
                        <div className="msg-info-name">Agent Zena</div>
                      </div>
                      <div className="msg-text">
                        {ct.loading ? <Loading /> :
                          // <ReactMarkdown remarkPlugins={[remarkGfm]}>{ct.server}</ReactMarkdown>
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              a: ({ node, ...props }) => (
                                <a {...props} target="_blank" rel="noopener noreferrer" />
                              ),
                            }}
                          >
                            {ct.server}
                          </ReactMarkdown>
                        }
                      </div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="msger-inputarea-main">
          <Form form={form} autoComplete="off" className="msger-inputarea" onFinish={handleSubmit}>
            <Form.Item name="message">
              <Input
                disabled={loading}
                className="msger-input"
                placeholder="Ask anything"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus={!isMobile}
              />
            </Form.Item>
            <button
              type={query.trim() ? "submit" : "button"}
              className="msger-send-btn"
              disabled={loading}
              onClick={
                !query.trim() && source === "talha"
                  ? () => window.open("https://voice.ormeagent.com/", "_blank")
                  : undefined
              }
            >
              {query.trim() || source === "arham" || source === "Postgres" || source === "supabase" ? (
                <FontAwesomeIcon icon={faPaperPlane} />
              ) : (
                <img src={agentai} width={36} alt="Voice" />
              )}
            </button>

          </Form>
        </div>

      </section>
    </div>
  );
}

export default VoiceBot;
